import axios, { AxiosRequestConfig } from "axios";
import { KokattoServiceClient } from "./KokattoServiceClient";
import { KokattoErrorResponse } from "./responses/KokattoErrorResponse";
import {
  OutboundCallAnswerRequest,
  OutboundCallInitiateRequest,
} from "./requests/OutboundCallRequest";
import {
  OutboundCallAnswerResponse,
  OutboundCallInitiateResponse,
} from "./responses/OutboundCallResponse";

import {
  CallRoomListModel,
  CallRoomModel,
  CallRoomSessionModel,
  RoomType,
} from "@/models/InboundCall/CallRoomModel";
import {
  InboundCallAcceptRequest,
  InboundCallPreAcceptRequest,
  InboundCallRejectIncomingRequest,
} from "./requests/InboundCallRejectIncomingRequest";
import { CallSessionModel } from "@/models/InboundCall/CallSessionModel";
import {
  GetCallRoomTokenRequest,
  HoldCallRoomRequest,
  JoinCallRoomRequest,
  LeaveCallRoomRequest,
  Pagination,
  RequestAssignCStoCallRoom,
  RequestCallRoomChat,
  RequestCallRoomList,
  RequestCloseCallRoom,
  RequestGetAudioUrl,
  RequestGetCallPermission,
  RequestListMessage,
  RequestPostCallPermission,
  RequestReport,
  RequestTransferCallRoom,
  RequestUpdateCallRoom,
  ResponsePagination,
} from "./requests/InboundCallRequest";
import { CallMessageModel } from "@/models/InboundCall/CallMessageModel";
import { KokattoGetPresignedUrlResponse } from "./responses/KokattoPresignedUrlResponse";
import {
  GetAudioUrlResponse,
  GetCallCenterConfigResponse,
  GetCallRoomTokenResponse,
  GetDurationTotalResponse,
  GetVoiceProviderResponse,
  GetRequestCallPermissionResponse,
  HoldCallRoomResponse,
  JoinCallRoomResponse,
  LeaveCallRoomResponse,
  TransferCallRoomResponse,
} from "./responses/InboundCallResponse";
import { ExportHistoryModel } from "@/models/InboundCall/ExportHistoryModel";
import { KokattoBaseResponse } from "./responses/KokattoBaseResponse";

export class InboundCallServiceClient extends KokattoServiceClient {
  constructor(
    args?:
      | string
      | {
          baseUrl?: string;
          token?: string;
        }
  ) {
    super(args);
    this.baseUrl = process.env.VUE_APP_BASE_URL_INBOUND_CALL_SERVICE ?? "";
  }

  async rejectIncomingCall(
    request: InboundCallRejectIncomingRequest
  ): Promise<CallRoomSessionModel | KokattoErrorResponse> {
    if (request?.roomType === undefined)
      throw new Error("Room type is required");
    let url = "";
    if (request?.roomType === RoomType.WHATSAPP) {
      url = `${this.baseUrl}/call/meta/reject`;
    } else {
      url = `${this.baseUrl}/call/${request?.roomType}/reject`;
    }

    return this.failsafeHttpRequest(async () => {
      const config: AxiosRequestConfig = {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      };

      const response = await axios.post(url, request, config);
      return response.data;
    });
  }

  async preAcceptCall(
    request: InboundCallPreAcceptRequest
  ): Promise<CallSessionModel> {
    console.log("InboundCall baseUrl", this.baseUrl);
    const url = `${this.baseUrl}/call/pre-accept`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async acceptCall(
    request: InboundCallPreAcceptRequest
  ): Promise<CallSessionModel> {
    const url = `${this.baseUrl}/call/accept`;

    return this.failsafeHttpRequest(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async terminateCall(
    request: InboundCallRejectIncomingRequest
  ): Promise<CallSessionModel> {
    const url = `${this.baseUrl}/call/meta/terminate`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async getRoomSessionList(
    data: RequestCallRoomList
  ): Promise<ResponsePagination<CallRoomSessionModel>> {
    // Retrieves a paginated list of call room sessions based on the provided filters.
    // @param data - The request parameters including pagination details and optional filters.
    const url = `${this.baseUrl}/call/list`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        params: data,
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data.data;
    });
  }

  async getRoomHistory(
    data: RequestCallRoomList
  ): Promise<ResponsePagination<CallRoomModel>> {
    // Retrieves a paginated list of call room sessions based on the provided filters.
    // @param data - The request parameters including pagination details and optional filters.
    const url = `${this.baseUrl}/call/room/list`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        params: data,
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async getListMessageRoom(
    data: RequestListMessage
  ): Promise<ResponsePagination<CallMessageModel>> {
    // Retrieves a paginated list of messages for a specific call room.
    // @param data - The request parameters including room ID and pagination details.
    const url = `${this.baseUrl}/call/message/list`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        params: data,
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async closeCallRoom(data: RequestCloseCallRoom): Promise<CallRoomModel> {
    // Closes a call room, indicating that the session has ended.
    // @param data - The request parameters including customer phone number and room ID.
    const url = `${this.baseUrl}/call/room/close`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, data, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data.data;
    });
  }

  async assignCStoRoom(
    data: RequestAssignCStoCallRoom
  ): Promise<CallRoomModel> {
    // Assigns a customer service representative to a specific call room.
    // @param data - The request parameters including customer phone number, room ID, and CS ID.
    const url = `${this.baseUrl}/call-room/assign`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.put(url, data, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data.content;
    });
  }

  async sendCallRoomChat(data: RequestCallRoomChat): Promise<CallMessageModel> {
    // Sends a chat message within a call room.
    // @param data - The request parameters including room ID, message content, and sender information.
    const url = `${this.baseUrl}/call/room/create-message`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, data, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data.data;
    });
  }

  async getUploadPresignedUrl(
    filename: string
  ): Promise<KokattoGetPresignedUrlResponse> {
    const url = `${this.baseUrl}/record-audio/presign/upload`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: {
          filename: filename,
        },
      });
      return response.data;
    });
  }

  async uploadRecordingAudio(url: string, file: Blob): Promise<void> {
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.put(url, file, {
        headers: {
          "Content-Type": "audio/ogg",
        },
      });
      return response.data;
    });
  }

  async getDurationTotal(roomId: string): Promise<GetDurationTotalResponse> {
    const url = `${this.baseUrl}/call/duration`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: {
          roomId,
        },
      });
      return response.data.content;
    });
  }

  async postRequestReport(payload: RequestReport) {
    const url = `${this.baseUrl}/export-history/request`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(
        url,
        {
          requestStartDate: payload.startDate,
          requestEndDate: payload.endDate,
          type: payload.type,
          roomType: payload.roomType,
        },
        {
          headers: {
            authorization: `Bearer ${this.token}`,
          },
        }
      );

      return response.data.content;
    });
  }

  async getExportHistoryRequest(params: Pagination & { roomType: RoomType }) {
    const url = `${this.baseUrl}/export-history/list`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: params,
      });

      return response.data;
    });
  }

  async getExportHistoryUrlForDownload(item: ExportHistoryModel) {
    const url = `${this.baseUrl}/export-history/report/signed`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: {
          type: item.type,
          filename: item.filename,
        },
      });
      return response.data.presignUrl;
    });
  }
  async getRecordPresignUrl(sessionId: string): Promise<string> {
    const url = `${this.baseUrl}/record-audio/presign/download`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: {
          filename: sessionId + ".ogg",
        },
      });
      return response.data.presignUrl;
    });
  }

  async inboundCallAccept(
    request: InboundCallAcceptRequest
  ): Promise<KokattoBaseResponse> {
    const url = `${this.baseUrl}/call/meta/accept`;
    return this.failsafeHttpRequest(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async holdCallWhatsapp(
    request: HoldCallRoomRequest
  ): Promise<HoldCallRoomResponse> {
    const url = `${this.baseUrl}/call/meta/hold`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  /**START INBOUND CALL CENTER (LIVEKIT) SERVICE CLIENT */

  async getCallRoomToken(
    request: GetCallRoomTokenRequest
  ): Promise<GetCallRoomTokenResponse> {
    const url = `${this.baseUrl}/call/phone/token`;
    return this.failsafeHttpRequest(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async joinCallRoom(
    request: JoinCallRoomRequest
  ): Promise<JoinCallRoomResponse> {
    const url = `${this.baseUrl}/call/phone/accept`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async leaveCallRoom(
    request: LeaveCallRoomRequest
  ): Promise<LeaveCallRoomResponse> {
    const url = `${this.baseUrl}/call/phone/terminate`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }
  async holdCallPhone(
    request: HoldCallRoomRequest
  ): Promise<HoldCallRoomResponse> {
    const url = `${this.baseUrl}/call/phone/hold`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async updateCallRoom(data: RequestUpdateCallRoom) {
    const url = `${this.baseUrl}/call/room/update-description`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, data, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }
  async transferCallRoom(
    request: RequestTransferCallRoom
  ): Promise<TransferCallRoomResponse> {
    let url = "";
    if (request?.roomType === RoomType.WHATSAPP) {
      url = `${this.baseUrl}/call/meta/transfer`;
    } else {
      url = `${this.baseUrl}/call/${request?.roomType}/transfer`;
    }
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async getAudioRecordingUrl(
    request: RequestGetAudioUrl
  ): Promise<GetAudioUrlResponse> {
    const url = `${this.baseUrl}/call/recording-url`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: request,
      });
      return response.data.data;
    });
  }

  async getListVoiceProvider(): Promise<GetVoiceProviderResponse> {
    const url = `${this.baseUrl}/voice-provider`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: { authorization: `Bearer ${this.token}` },
      });
      return response.data;
    });
  }

  async getCallPermissionUser(
    request: RequestGetCallPermission
  ): Promise<GetRequestCallPermissionResponse> {
    const url = `${this.baseUrl}/call/permission`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
        params: request,
      });

      return response.data;
    });
  }

  async postCallPermissionUser(
    request: RequestPostCallPermission
  ): Promise<void> {
    const url = `${this.baseUrl}/call/permission/message`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
          "content-type": "application/json",
        },
      });
      return response.data;
    });
  }

  async initiateOutboundCall(
    request: OutboundCallInitiateRequest
  ): Promise<OutboundCallInitiateResponse> {
    const url = `${this.baseUrl}/call/meta/initiate`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async sendOutboundCallAnswer(
    request: Partial<OutboundCallAnswerRequest>,
    action: "answer" | "hold" | "terminate"
  ): Promise<OutboundCallAnswerResponse> {
    const url = `${this.baseUrl}/call/meta/initiate/${action}`;

    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.post(url, request, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }

  async getCallCenterConfig(): Promise<GetCallCenterConfigResponse> {
    const url = `${this.baseUrl}/call-strategy`;
    return this.failsafeHttpRequestThrowable(async () => {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    });
  }
}
